import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import vw from "../api/virtualWorld.api";

const SEEN_KEY = "fc_vw_transfer_offer_seen_v1";

function loadSeen() {
  try {
    return JSON.parse(sessionStorage.getItem(SEEN_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function saveSeen(map) {
  try {
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function offerToastId(offerOrId, status) {
  const id = typeof offerOrId === "object"
    ? String(offerOrId?._id || offerOrId?.id || "")
    : String(offerOrId || "");
  if (!id || !status) return undefined;
  return `transfer-${id}-${status}`;
}

/** Sofort-Toast auf der Transfers-Seite: Poller soll nicht nochmal feuern. */
export function markTransferOfferSeen(offerOrId, status) {
  const id = typeof offerOrId === "object"
    ? String(offerOrId?._id || offerOrId?.id || "")
    : String(offerOrId || "");
  if (!id || !status) return;
  const map = loadSeen();
  map[id] = status;
  saveSeen(map);
}

/**
 * Nach erfolgreichem Kauf: ältere Ablehnungs-Toasts zum selben Spieler nicht mehr zeigen.
 */
export function markSiblingOffersSeen(outgoing = [], playerKey, preferStatus = "accepted") {
  if (!playerKey) return;
  const map = loadSeen();
  for (const o of outgoing) {
    if (String(o.targetPlayerKey || "") !== String(playerKey)) continue;
    const id = offerId(o);
    if (!id) continue;
    map[id] = o.status || preferStatus;
    const rejectTid = offerToastId(id, "rejected");
    if (rejectTid && toast.isActive(rejectTid)) toast.dismiss(rejectTid);
  }
  saveSeen(map);
}

function offerId(o) {
  return String(o._id || o.id || "");
}

function hasNewerAcceptedSibling(offer, outgoing) {
  const key = String(offer.targetPlayerKey || "");
  if (!key) return false;
  const myUpdated = offer.updatedAt ? new Date(offer.updatedAt).getTime() : 0;
  const myCreated = offer.createdAt ? new Date(offer.createdAt).getTime() : 0;
  const myTime = Math.max(myUpdated, myCreated);
  return (outgoing || []).some((o) => {
    if (offerId(o) === offerId(offer)) return false;
    if (String(o.targetPlayerKey || "") !== key) return false;
    if (o.status !== "accepted") return false;
    const t = Math.max(
      o.updatedAt ? new Date(o.updatedAt).getTime() : 0,
      o.createdAt ? new Date(o.createdAt).getTime() : 0
    );
    return t >= myTime - 2000;
  });
}

function toastForOffer(offer, outgoing = []) {
  const name = offer.targetName || "Spieler";
  const note = offer.decisionNote || offer.reason || "";
  const toastId = offerToastId(offer, offer.status);
  if (!toastId || toast.isActive(toastId)) return;

  // Kauf hat geklappt → keine irreführende Ablehnung zum selben Spieler
  if (offer.status === "rejected" && hasNewerAcceptedSibling(offer, outgoing)) {
    return;
  }

  if (offer.status === "accepted") {
    toast.success(note || `Transfer angenommen: ${name}`, {
      toastId,
      autoClose: 8000,
    });
    return;
  }
  if (offer.status === "rejected") {
    toast.warning(note || `Transfer abgelehnt: ${name}`, {
      toastId,
      autoClose: 10000,
    });
    return;
  }
  if (offer.status === "expired") {
    toast.info(note || `Angebot für ${name} abgelaufen`, {
      toastId,
      autoClose: 7000,
    });
  }
}

/**
 * Toast-Popups wenn eigene Transfer-Angebote angenommen/abgelehnt werden
 * (inkl. Begründung aus decisionNote).
 */
export default function useTransferOfferToasts(userId, enabled = true) {
  const statusRef = useRef({});
  const readyRef = useRef(false);

  useEffect(() => {
    if (!userId || !enabled) return undefined;

    const seen = loadSeen();
    statusRef.current = { ...seen };
    readyRef.current = false;

    const poll = async () => {
      try {
        const res = await vw.clubOffers(userId);
        const outgoing = res.data?.outgoing || [];
        // sessionStorage kann von der Transfers-Seite aktualisiert worden sein
        const fromStorage = loadSeen();
        statusRef.current = { ...statusRef.current, ...fromStorage };
        const nextMap = { ...statusRef.current };

        for (const offer of outgoing) {
          const id = offerId(offer);
          if (!id) continue;
          const prev = statusRef.current[id];
          const status = offer.status || "open";

          if (!readyRef.current) {
            nextMap[id] = status;
            continue;
          }

          if (prev === status) {
            nextMap[id] = status;
            continue;
          }

          // Nur echte Übergänge open → entschieden toasten.
          // Unbekannte alte Rejects (z. B. nach Reload/Doppelgebot) nicht nachfeuern —
          // sonst „unterschreibt“ + „lehnt ab“ zum selben Spieler.
          if (prev === "open" && status !== "open") {
            toastForOffer(offer, outgoing);
          } else if (!prev && status === "accepted") {
            const updated = offer.updatedAt ? new Date(offer.updatedAt).getTime() : 0;
            if (updated && Date.now() - updated < 5 * 60 * 1000) {
              toastForOffer(offer, outgoing);
            }
          }
          nextMap[id] = status;
        }

        statusRef.current = nextMap;
        saveSeen(nextMap);
        readyRef.current = true;
      } catch {
        /* still */
      }
    };

    poll();
    const t = setInterval(poll, 15000);
    return () => clearInterval(t);
  }, [userId, enabled]);
}
